import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {COPILOT_APP_SLUG} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import type {Session} from '../types/session'
import type {HydratedAPITask} from '../types/api-task'
import {DefaultTrailingVisual} from './profiles/DefaultTrailingVisual'
import type {TrailingVisualSlotProps} from './agent-profile-types'
import type {Icon} from '@primer/octicons-react'
import {CopilotIcon, TerminalIcon, VscodeIcon} from '@primer/octicons-react'

/**
 * Props passed to the chat input slot component.
 * Kept minimal — each implementation can accept additional props via its own interface,
 * but this is the contract the shell component programs against.
 */
export interface ChatInputSlotProps {
  sessionId: string
  session: Session
  task?: HydratedAPITask | null
  textAreaRef?: React.Ref<HTMLTextAreaElement>
  onCommandSent?: () => void
  cliRemoteNoticeDismissed?: boolean
  remoteControlPolicyEnabled?: boolean
  ssoOrgIds?: string[]
}

/**
 * Props passed to the leading icon slot component.
 */
export interface LeadingIconSlotProps {
  state?: string
  prState?: string
  prInMergeQueue?: boolean
  isArchived?: boolean
  isOptimistic?: boolean
  session?: Session | null
  staleAt?: string
  dataUpdatedAt?: number
  sharingStatus?: 'unshared' | 'shared'
}

/**
 * Component slots for agent-type-specific rendering.
 * Use `null` to indicate "don't render this slot" (e.g., code review agents have no ChatInput).
 */
export interface AgentProfileSlots {
  /** Icon shown in task lists and headers. When provided, overrides the default state-based icon logic. */
  LeadingIcon: React.ComponentType<LeadingIconSlotProps> | null
  /** Chat input component shown at the bottom of the task view. null = no input (e.g. code review). */
  ChatInput: React.ComponentType<ChatInputSlotProps> | null
  /** Component shown when a session is in_progress but has no logs yet. null = use default behavior. */
  EmptyState: React.ComponentType | null
  /** Environment indicator shown as a trailing visual in task lists */
  TrailingVisual: React.ComponentType<TrailingVisualSlotProps> | null
}

/**
 * A complete agent behavior profile. Controls both simple boolean flags
 * and complex rendering via component slots.
 *
 * Adding a new agent type = create a Partial<AgentProfile> with only the overrides.
 * Unknown agents get DEFAULT_PROFILE automatically.
 */
export interface AgentProfile {
  /** Unique slug for this agent profile (e.g., 'copilot-developer-cli') */
  slug: string
  /** Human-readable name */
  displayName: string

  /**
   * Whether this agent type always has exactly one session per task.
   * When true, the per-session collapsible header is hidden (no need
   * for a divider) and session content is always expanded.
   */
  singleSessionAgent: boolean

  /**
   * Whether session data is private to the creator and cannot be meaningfully shared.
   * When true, the copy-link button is hidden in the task chat header.
   */
  privateSessions: boolean

  /**
   * Whether the agent provides its own intent/status messages as part of its event stream.
   * When true, synthetic UI-generated status messages ("Agent is booting up…",
   * "Agent is working…", "started a task/review") are suppressed.
   */
  agentProvidesIntent: boolean

  /**
   * Whether the session is a live interactive conversation (e.g. CLI).
   * Enables optimistic log entries for prompt responses and
   * display of raw message content for unmatched commands.
   */
  interactiveSession: boolean

  /** Whether to enable sub-agent log grouping (requires feature flag too) */
  enableAgentGrouping: boolean

  /** Label for the client name shown in task lists (e.g. "CLI", "VS Code") */
  secondaryLabel: string | null

  /**
   * Hardcoded agent info. When non-null, `useAgentInfo` can skip the server fetch.
   * When null, agent info is fetched from the monolith as usual.
   */
  agentInfo: AgentInfo | null

  slots: AgentProfileSlots
}

/**
 * The default agent profile. Represents standard behavior for the Copilot Cloud Agent
 * and serves as the fallback for unknown/third-party agent types.
 *
 * All boolean flags default to the "standard" behavior. Component slots default to null,
 * meaning the standard rendering path is used (no override).
 */
export const DEFAULT_PROFILE: AgentProfile = {
  slug: 'default',
  displayName: 'Agent',

  singleSessionAgent: false,

  privateSessions: false,

  agentProvidesIntent: false,
  interactiveSession: false,

  enableAgentGrouping: false,

  secondaryLabel: null,

  agentInfo: null,

  slots: {
    LeadingIcon: null,
    ChatInput: null, // null here means "use the default input component" — resolved by the consumer
    EmptyState: null,
    TrailingVisual: DefaultTrailingVisual,
  },
}

/**
 * Merges a partial profile with the default, correctly deep-merging the `slots` object.
 */
export function mergeWithDefault(overrides: Partial<AgentProfile>): AgentProfile {
  return {
    ...DEFAULT_PROFILE,
    ...overrides,
    slots: {
      ...DEFAULT_PROFILE.slots,
      ...overrides.slots,
    },
  }
}

/**
 * Registry mapping agent slugs to their filter icons.
 * Agents not in this registry will use their avatar URL instead.
 */
export const FILTER_ICON_REGISTRY: Record<string, Icon> = {
  'copilot-swe-agent': CopilotIcon,
  'copilot-developer': CopilotIcon,
  'copilot-developer-cli': TerminalIcon,
  'copilot-developer-app': TerminalIcon,
  'vscode-chat': VscodeIcon,
}

/**
 * Look up the filter icon for a given agent slug.
 * Returns undefined when no icon is registered (use avatar URL instead).
 */
export function getFilterIconForSlug(slug?: string): Icon | undefined {
  if (slug === COPILOT_APP_SLUG && !copilotFeatureFlags.appRemoteSessions) return undefined
  if (slug && FILTER_ICON_REGISTRY[slug]) {
    return FILTER_ICON_REGISTRY[slug]
  }
  return undefined
}
